// @flow
// ----------------------------------------------------------------------
// <copyright file='Izenda.ReportViewerToolbar.jsx' company='Izenda'>
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import AuthorizationValidator from 'share/components/hoc/Izenda.AuthorizationValidator';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import Button from 'share/components/controls/Izenda.Button';
import Alerts from 'modules/ReportDesigner/Toolbar/Izenda.ReportDesignerAlerts';
import ActionButton from 'share/components/controls/Izenda.ActionButton';
import actions from './Izenda.ReportViewerToolbarActions';
import classnames from 'classnames';
import { ACCESS_RIGHT, PERMISSION_KEY } from 'logic/data/Izenda.AuthorizationConstants';
import { LICENSE_KEY } from 'logic/data/Izenda.LicenseCheckerConstants';
import ReportButtons from '../Buttons/Izenda.ReportButtons';
import { invokeArgs, get } from 'lodash/fp';

const EmailButton = AuthorizationValidator()(Button);
const AuthorizationButton = AuthorizationValidator()(Button);
const ExportReportBtn = AuthorizationValidator()(props => {
  return <ReportButtons.ExportReportButton {...props} />;
});
const PrintBtn = AuthorizationValidator()(props => {
  return <ReportButtons.PrintReportButton {...props} />;
});
EmailButton.displayName = 'EmailButton';
AuthorizationButton.displayName = 'AuthorizationButton';
ExportReportBtn.displayName = 'ExportReportBtn';

@connect(actions)
export default class ReportViewerToolbar extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    reportId: PropTypes.string.isRequired
  };

  constructor(props: any) {
    super(props);
    const displayMode = props.fieldStore.reportStore.model.displayMode;
    this.toggleSubscription = displayMode.toggleSubscription.bind(displayMode);
    this.exposeIntegrationMethod();
  }

  componentWillReceiveProps(nextProps: any) {
    if (
      this.props.registerExposeMethod !== nextProps.registerExposeMethod ||
      this.props.updateResults !== nextProps.updateResults
    ) {
      this.removeIntegrationMethod();
      this.exposeIntegrationMethod();
    }
  }

  componentWillUnmount() {
    this.removeIntegrationMethod();
  }

  componentDidMount() {
    const reportStore = this.props.fieldStore.reportStore;
    reportStore.alertDetails.reset();
  }

  removeIntegrationMethod() {
    invokeArgs('unregisterExposeMethod', [['updateResults']], this.props);
  }

  exposeIntegrationMethod() {
    const method = {
      updateResults: this.props.updateResults.bind(null, null)
    };

    invokeArgs('registerExposeMethod', [method], this.props);
  }

  /**
   * Check save permission.
   */
  checkPermissionOfSave() {
    const {
      authorization,
      authorizationConstants: { PERMISSION_KEY }
    } = this.props;

    return authorization.checkPrivileges([PERMISSION_KEY.ReportsCanCreateNewReportValue]);
  }

  render() {
    const props = this.props;
    const {
      localization,
      store,
      fieldStore,
      authorization,
      editMode,
      reportId,
      designerStore,
      boardDetail,
      location,
      reportSubscriptionStore
    } = props;

    const {
      reportStore,
      reportStore: {
        model: { displayMode, accessPriority, active }
      }
    } = fieldStore;
    const { reportName, reportType, selectedCategoryId } = store;
    const viewMode = !editMode;
    const doesShowSaveButton = this.checkPermissionOfSave();
    const hasScheduleLicense = authorization.checkLicense(LICENSE_KEY.Scheduling);
    const hasExportLicense = authorization.checkLicense(LICENSE_KEY.Exporting);

    const subscribeClassName = classnames({
      'izenda-Button_active': displayMode.isSubscriptionMode
    });

    const { message, alertType, key: alertKey, success, errors, showType, type } = reportStore.alertDetails;

    return (
      <div className='izenda-Wrapper-header'>
        <div className='izenda-Alert-container'>
          <Alerts
            message={message}
            alertType={alertType}
            alertKey={alertKey}
            success={success}
            errors={errors}
            type={type}
            showType={showType}
          />
        </div>

        <div className='izenda-ActionBarRight'>
          <ActionButton>
            {props.checkDisplayShowHideHeaderFooter(fieldStore) && (
              <Button
                onClick={props.onDisplayShowHideHeaderFooter}
                label={props.getShowHideHeaderFooterLabel(designerStore, localization)}
              />
            )}

            {(accessPriority === ACCESS_RIGHT.FullAccess ||
              accessPriority === ACCESS_RIGHT.QuickEdit ||
              accessPriority === ACCESS_RIGHT.SaveAs) &&
              active &&
              !viewMode && (
                <ReportButtons.ViewReportButton
                  reportId={reportId}
                  disabled={viewMode}
                  store={store}
                  location={location}
                />
              )}
            {hasScheduleLicense && (
              <AuthorizationButton
                label={localization.getText('COMMON_LABEL_SUBSCRIBE')}
                className={subscribeClassName}
                onClick={this.toggleSubscription}
                keys={[PERMISSION_KEY.ReportsActionsSubscribe]}
              />
            )}
            {hasExportLicense && (
              <PrintBtn
                name={reportName}
                store={store}
                print={store.printReport}
                designerStore={designerStore}
                id={reportId}
                disabled={displayMode.doesDisablePrintBtn}
                keys={[PERMISSION_KEY.ReportsActionsPrint]}
              />
            )}
            <EmailButton
              label={localization.getText('COMMON_LABEL_EMAIL')}
              onClick={props.openEmailPopup}
              disabled={displayMode.doesDisableEmailBtn}
              keys={[PERMISSION_KEY.ReportsActionsEmail]}
            />
            {hasExportLicense && (
              <ExportReportBtn
                name={reportName}
                store={store}
                export={store.exportReport}
                designerStore={designerStore}
                id={reportId}
                disabled={displayMode.doesDisableExportBtn}
                keys={[PERMISSION_KEY.ReportsActionsExporting]}
              />
            )}
            <ReportButtons.EditReportButton
              store={store}
              reportId={reportId}
              reportType={reportType}
              categoryId={selectedCategoryId}
              viewMode={viewMode}
              boardDetail={boardDetail}
              confirmFn={props.confirmFn}
              onSave={props.onSave}
              disabled={displayMode.doesDisableEditBtn}
              location={location}
              fieldStore={fieldStore}
              accessPriority={accessPriority}
              active={active}
            />
            {accessPriority !== ACCESS_RIGHT.Locked && (
              <ReportButtons.UpdateReportButton
                updateResults={props.updateResults}
                disabled={displayMode.doesDisableUpdateResultBtn}
              />
            )}

            {doesShowSaveButton &&
              (accessPriority === ACCESS_RIGHT.FullAccess ||
                accessPriority === ACCESS_RIGHT.QuickEdit ||
                accessPriority === ACCESS_RIGHT.SaveAs) && (
                <ReportButtons.SaveReportButton
                  reportStore={store}
                  fieldStore={fieldStore}
                  subscribedInstancesStore={reportSubscriptionStore}
                  onSave={props.onSave}
                  viewMode={viewMode}
                  confirmFn={props.confirmFn}
                  accessPriority={accessPriority}
                  boardDetail={boardDetail}
                  active={active}
                  keepDisplay
                />
              )}

            {accessPriority !== ACCESS_RIGHT.Locked && (
              <ReportButtons.CancelViewReportButton
                confirmFn={props.confirmFn}
                checkDirty={props.checkDirty}
                cancelReport={props.cancelReport}
                keepDisplay
              />
            )}

            <ReportButtons.CloseViewReportButton
              reportId={reportId}
              reportType={reportType}
              categoryId={selectedCategoryId}
              fieldStore={fieldStore}
              location={location}
              store={store}
            />
          </ActionButton>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Reports/ReportViewer/Izenda.ReportViewerToolbar.js